import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { customersAPI, dnoAPI } from '../lib/api';
import {
  Zap,
  Upload,
  CheckCircle2,
  Loader2,
  Save,
  CalendarCheck,
  CalendarClock,
  Hash,
  Send,
  Paperclip,
  ArrowUpRight,
  ArrowDownLeft,
  FileCheck2,
  History,
} from 'lucide-react';

/**
 * DNO Application Details card on the Customer Detail page.
 *
 * Shows DNO reference + cut-out image + key dates (submitted / confirmed /
 * commissioned). The fields are auto-populated by the backend whenever a
 * linked DNO application moves through ENA Connect Direct, but admins can
 * override any field on the customer record (e.g. for legacy applications
 * that were filed outside the CRM).
 */
export const DnoApplicationCard = ({ customer, isAdmin, onUpdated }) => {
  const navigate = useNavigate();
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comms, setComms] = useState([]);
  const [form, setForm] = useState({
    dno_reference: '',
    dno_cutout_image_url: '',
    dno_application_submitted_date: '',
    dno_application_confirmed_date: '',
    dno_commissioning_confirmed_date: '',
  });

  useEffect(() => {
    setForm({
      dno_reference: customer?.dno_reference || '',
      dno_cutout_image_url: customer?.dno_cutout_image_url || '',
      dno_application_submitted_date: customer?.dno_application_submitted_date || '',
      dno_application_confirmed_date: customer?.dno_application_confirmed_date || '',
      dno_commissioning_confirmed_date: customer?.dno_commissioning_confirmed_date || '',
    });
    setDirty(false);
  }, [customer?.id, customer?.dno_reference, customer?.dno_cutout_image_url, customer?.dno_application_submitted_date, customer?.dno_application_confirmed_date, customer?.dno_commissioning_confirmed_date]);

  useEffect(() => {
    if (!customer?.id) return;
    dnoAPI.getComms(customer.id)
      .then((r) => setComms(r.data?.items || []))
      .catch(() => setComms([]));
  }, [customer?.id, customer?.dno_reference, customer?.dno_application_submitted_date]);

  const update = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleUploadCutOut = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await dnoAPI.uploadAttachment(file, 'image');
      const data = res.data || {};
      const url = data.fileUrl || data.file_url || data.url || data.attachmentUrl || data.attachment_url || '';
      if (!url) {
        toast.error('Cut-out upload succeeded but no file URL returned');
        return;
      }
      update('dno_cutout_image_url', url);
      toast.success('Cut-out image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await customersAPI.update(customer.id, form);
      toast.success('DNO details saved');
      setDirty(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save DNO details');
    } finally {
      setSaving(false);
    }
  };

  const isAutoFilled = (key) => !!customer?.[key] && form[key] === (customer[key] || '');

  return (
    <Card className="border-slate-200" data-testid="dno-application-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600" />
            DNO Application Details
            {customer?.dno_reference && (
              <span className="ml-2 text-xs font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                Linked
              </span>
            )}
          </CardTitle>
          {/* "Raise DNO" jumps to the DNO Applications wizard with this
              customer pre-selected and step 2 open. Visible to admins for
              every customer regardless of whether an application already
              exists (allows raising replacement / amendment applications). */}
          {isAdmin && (
            <Button
              size="sm"
              variant={customer?.dno_reference ? 'outline' : 'default'}
              className={customer?.dno_reference
                ? ''
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }
              onClick={() => navigate(`/dno-applications?customer_id=${customer?.id}&new=1`)}
              data-testid="raise-dno-btn"
              title="Raise a new DNO application with ENA Connect Direct"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {customer?.dno_reference ? 'Raise another DNO' : 'Raise DNO Application'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* DNO Reference */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="dno-reference" className="text-xs flex items-center gap-1.5 text-slate-600">
              <Hash className="w-3.5 h-3.5" />
              DNO Reference
              {isAutoFilled('dno_reference') && (
                <span className="text-[10px] text-emerald-600 ml-auto">auto-synced</span>
              )}
            </Label>
            <Input
              id="dno-reference"
              value={form.dno_reference}
              onChange={(e) => update('dno_reference', e.target.value)}
              placeholder="Auto-pulled from DNO application"
              disabled={!isAdmin}
              data-testid="dno-reference-input"
            />
            {!isAdmin && customer?.dno_reference && (
              <p className="text-[11px] text-slate-400">Admin can override this</p>
            )}
          </div>

          {/* Cut-out image */}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5 text-slate-600">
              <Upload className="w-3.5 h-3.5" />
              Cut-out image
            </Label>
            {form.dno_cutout_image_url ? (
              <div className="flex items-center gap-3 p-2 bg-emerald-50 border border-emerald-200 rounded">
                <img
                  src={form.dno_cutout_image_url}
                  alt="Cut-out"
                  className="w-16 h-16 object-cover rounded border border-emerald-300 bg-white"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  data-testid="dno-cutout-thumb"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-emerald-700 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Uploaded
                  </div>
                  <p className="text-[10px] text-slate-500 truncate">{form.dno_cutout_image_url}</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadCutOut}
                  className="hidden"
                  id={`dno-cutout-replace-${customer?.id}`}
                  data-testid="dno-cutout-replace"
                />
                <label
                  htmlFor={`dno-cutout-replace-${customer?.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-slate-300 bg-white rounded cursor-pointer hover:bg-slate-50"
                >
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Replace
                </label>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 border-2 border-dashed border-slate-300 rounded bg-slate-50">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadCutOut}
                  className="text-xs"
                  data-testid="dno-cutout-upload"
                />
                {uploading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
            )}
          </div>
        </div>

        {/* Dates row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DateField
            label="Application submitted"
            icon={<CalendarClock className="w-3.5 h-3.5" />}
            value={form.dno_application_submitted_date}
            onChange={(v) => update('dno_application_submitted_date', v)}
            autoFilled={isAutoFilled('dno_application_submitted_date')}
            testId="dno-submitted-date"
          />
          <DateField
            label="Application confirmation received"
            icon={<CalendarCheck className="w-3.5 h-3.5" />}
            value={form.dno_application_confirmed_date}
            onChange={(v) => update('dno_application_confirmed_date', v)}
            autoFilled={isAutoFilled('dno_application_confirmed_date')}
            testId="dno-confirmed-date"
          />
          <DateField
            label="Commissioning confirmation"
            icon={<CalendarCheck className="w-3.5 h-3.5" />}
            value={form.dno_commissioning_confirmed_date}
            onChange={(v) => update('dno_commissioning_confirmed_date', v)}
            autoFilled={isAutoFilled('dno_commissioning_confirmed_date')}
            testId="dno-commissioning-date"
          />
        </div>

        {/* DNO comms timeline — submitted / amended / replies */}
        {comms.length > 0 && (
          <div className="pt-3 border-t border-slate-100" data-testid="dno-comms-timeline">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5" /> DNO comms — sent to / from DNO@helioses.co.uk
            </p>
            <ol className="space-y-2">
              {comms.slice(0, 8).map((ev, i) => (
                <li key={i} className="flex items-start gap-2.5" data-testid={`dno-comm-${ev.type}-${i}`}>
                  <CommIcon type={ev.type} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-medium text-slate-800">{ev.label}</p>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtDateTime(ev.timestamp)}</span>
                    </div>
                    {ev.subject && (
                      <p className="text-[11px] text-slate-500 truncate">{ev.subject}</p>
                    )}
                    {ev.attachments?.length > 0 && (
                      <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                        <Paperclip className="w-3 h-3" />
                        {ev.attachments.join(' · ')}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Save bar */}
        {dirty && (
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mr-auto">Unsaved changes</p>
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-dno-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
              Save DNO details
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DateField = ({ label, icon, value, onChange, autoFilled, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs flex items-center gap-1.5 text-slate-600">
      {icon}
      {label}
      {autoFilled && value && (
        <span className="text-[10px] text-emerald-600 ml-auto">auto-synced</span>
      )}
    </Label>
    <Input
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    />
  </div>
);

const CommIcon = ({ type }) => {
  const base = 'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5';
  if (type === 'submitted') return <span className={`${base} bg-blue-100`}><FileCheck2 className="w-3.5 h-3.5 text-blue-600" /></span>;
  if (type === 'amended') return <span className={`${base} bg-amber-100`}><FileCheck2 className="w-3.5 h-3.5 text-amber-600" /></span>;
  if (type === 'reply_sent') return <span className={`${base} bg-emerald-100`}><ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" /></span>;
  return <span className={`${base} bg-slate-100`}><ArrowDownLeft className="w-3.5 h-3.5 text-slate-600" /></span>;
};

const fmtDateTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d)) return '';
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export default DnoApplicationCard;
